import { formatFigure, formatToFixedMoneyWithLocale, ZERO_DASH } from 'Helpers/formatService';

/**
 * IB-700 — shared presentation helpers for the redesigned Plan tab (register / Gantt / Kanban).
 * Colours come from tailwind.config.js; no new tokens are introduced.
 */

/** Full money figure prefixed with the budget's currency symbol (e.g. "€4,103,680"). */
export const money = (symbol, val) => (isNaN(val) ? $t('No Data') : `${symbol ?? ''}${formatToFixedMoneyWithLocale(val)}`);

/**
 * Money for a table cell: blank when no value is set, a symbol-less dash for an exact zero, and
 * the "<1" floor for a value that rounds to zero — otherwise the same figure as `money`.
 */
export const tableMoney = (symbol, val) => {
    const text = formatFigure(val);

    return text === '' || text === ZERO_DASH ? text : `${symbol ?? ''}${text}`;
};

/** Status dot colour by stage, matching the Overview pipeline (grey → blue → green → black). */
export const statusDotClass = (statusTitle) => {
    switch (statusTitle) {
        case 'PR Submitted':
            return 'tw-bg-blue-50';
        case 'RFP Issued':
        case 'Tender Evaluation':
            return 'tw-bg-blue';
        case 'Awarded':
        case 'In Progress':
            return 'tw-bg-green';
        case 'Complete':
            return 'tw-bg-black';
        case 'Not Started':
        default:
            return 'tw-bg-charcoal-25';
    }
};

/**
 * Status pill styling (tinted background + readable text), colour-anchored to `statusDotClass` so
 * the dot inside the pill matches the tint. A null status renders as a grey "Not Started" pill —
 * callers default the title to 'Not Started' rather than showing an em dash.
 */
export const statusPillClass = (statusTitle) => {
    switch (statusTitle) {
        case 'PR Submitted':
            return 'tw-bg-blue-10 tw-text-blue-75';
        case 'RFP Issued':
        case 'Tender Evaluation':
            return 'tw-bg-blue-15 tw-text-blue-75';
        case 'Awarded':
        case 'In Progress':
            return 'tw-bg-green-25 tw-text-charcoal-75';
        case 'Complete':
            return 'tw-bg-charcoal-15 tw-text-charcoal-75';
        case 'Not Started':
        default:
            return 'tw-bg-charcoal-10 tw-text-charcoal-75';
    }
};

/** Priority pill styling: High = red, Medium = blue, Low = grey (per the mockup). */
export const priorityPillClass = (priorityLabel) => {
    switch (priorityLabel) {
        case 'High':
            return 'tw-bg-red-25 tw-text-red';
        case 'Medium':
            return 'tw-bg-blue-15 tw-text-blue-75';
        case 'Low':
        default:
            return 'tw-bg-charcoal-15 tw-text-charcoal-75';
    }
};

/**
 * Raise-action availability is driven by the procurement STATUS (matching the mockup), NOT the
 * `can_add_pr` EXISTS-flag — which is false for a brand-new "Not Started" item (see the IB-700
 * register test docblock). Each rule is combined with the relevant module-enabled setting by the
 * caller so an off module always greys the button.
 */
export const canRaisePr = (statusTitle) => ['Not Started', 'Awarded'].includes(statusTitle);
export const canRaiseEa = (statusTitle) => ['PR Submitted', 'RFP Issued', 'Tender Evaluation', 'In Progress'].includes(statusTitle);
export const canRaiseDc = (statusTitle) => ['Not Started', 'Tender Evaluation', 'Awarded'].includes(statusTitle);

/**
 * IB-700 (v14) — the Kanban board's five columns are the APPROVAL-PROCESS steps, driven by each
 * procurement's real PR/EA/DC approval state (from the resource flags), NOT its sourcing status.
 * Order matters: an item is placed in its furthest-reached step (see `kanbanColumnFor`).
 */
export const KANBAN_COLUMNS = [
    { id: 'to_raise', label: $t('To raise'), dot: 'tw-bg-charcoal-25' },
    { id: 'pr_submitted', label: $t('PR submitted'), dot: 'tw-bg-blue-50' },
    { id: 'pr_approved', label: $t('PR approved'), dot: 'tw-bg-blue' },
    { id: 'ea_dc_submitted', label: $t('EA/DC submitted'), dot: 'tw-bg-dark-blue' },
    { id: 'approved_committed', label: $t('Approved & committed'), dot: 'tw-bg-green' },
];

/**
 * Map a procurement to its furthest-reached approval step, evaluated highest → lowest:
 *   1. Approved & committed — the EA/DC (purchase order) is approved.
 *   2. EA/DC submitted      — an EA or DC has been raised but not yet approved (covers a DC raised
 *                             straight from "Not Started" with no user-raised PR: the DC creates a
 *                             system-approved PR under the hood, and `has_order` fires first here).
 *   3. PR approved          — the PR is approved but no order has been raised yet.
 *   4. PR submitted         — a PR is raised and still awaiting approval.
 *   5. To raise             — everything else (no PR yet, or a rejected/withdrawn PR so a new one
 *                             may be raised).
 * Returns the column id (see KANBAN_COLUMNS).
 */
export const kanbanColumnFor = (item) => {
    if (item?.po_approved) {
        return 'approved_committed';
    }
    if (item?.has_order) {
        return 'ea_dc_submitted';
    }
    if (item?.pr_approved) {
        return 'pr_approved';
    }
    if (item?.pr_exists && item?.pr_open) {
        return 'pr_submitted';
    }
    return 'to_raise';
};
